import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import privateAxios from "../utils/axiosPrivate";
import {
  getArchivePricingTypesURL,
  getClientStatusURL,
  getDeletePricingTypesURL,
  getPicingGroupURL,
  getPicingPlanUpdateURL,
  getPicingPlanURL,
  getPricingPlanLogsURL,
  getPricingPlanLogsUserURL,
  getPricingTypesURL,
  getPricingUserPlansURL,
  getQuotesStatusUrl,
  getToggleActiveStatusURL,
  getToggleDefaultRiskBasedsURL,
  getvalidateriskrangeURL
} from "../utils/constants/apiEndPoints";
import { commonErrMsg, pricingPlan } from "../locales/en";
import { showToastType } from "../utils/constants/appConstants";
import { showToast } from "../utils/utils";

const initialState = {
  pricingPlanLoading: false,
  planGroup: [],
  pricingTypes: [],
  ppCustomerStatus: [],
  logsLoading: false,
  pricingPlanData: [],
  pricingGrpLoading: false,
  pricingUserPlansLoading: false
};

const pricingplanSlice = createSlice({
  name: "pricingPlan",
  initialState,
  reducers: {
    setPricingPlanLoading(state, action) {
      state.pricingPlanLoading = action.payload;
    },
    setPlanGroup(state, action) {
      state.planGroup = action.payload;
    },
    setPricingTypes(state, action) {
      state.pricingTypes = action.payload;
    },
    removePricingTypeById(state, action) {
      state.pricingTypes = state.pricingTypes.filter(
        (item) => item.id !== action.payload
      );
    },
    setPPCustomerStatus(state, action) {
      state.ppCustomerStatus = action.payload;
    },
    setLogsLoading(state, action) {
      state.logsLoading = action.payload;
    },
    setPricingGrpLoading(state, action) {
      state.pricingGrpLoading = action.payload;
    },
    setPricingUserPlansLoading(state, action) {
      state.pricingUserPlansLoading = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // AddCase For getPricingPlanData
      .addCase(getPricingPlanData.pending, (state) => {
        state.pricingPlanLoading = true;
      })
      .addCase(getPricingPlanData.fulfilled, (state, action) => {
        state.pricingPlanLoading = false;
        state.pricingPlanData = action.payload.data;
      })
      .addCase(getPricingPlanData.rejected, (state) => {
        state.pricingPlanLoading = false;
      })

      // AddCase For getPricingGroupThunk
      .addCase(getPricingGroupThunk.pending, (state) => {
        state.pricingGrpLoading = true;
      })
      .addCase(getPricingGroupThunk.fulfilled, (state) => {
        state.pricingGrpLoading = false;
      })
      .addCase(getPricingGroupThunk.rejected, (state) => {
        state.pricingGrpLoading = false;
      })

      // AddCase For getPricingUserPlansThunk
      .addCase(getPricingUserPlansThunk.pending, (state) => {
        state.pricingUserPlansLoading = true;
      })
      .addCase(getPricingUserPlansThunk.fulfilled, (state) => {
        state.pricingUserPlansLoading = false;
      })
      .addCase(getPricingUserPlansThunk.rejected, (state) => {
        state.pricingUserPlansLoading = false;
      })

      // AddCase For getPricingPlanLogsThunk
      .addCase(getPricingPlanLogsThunk.pending, (state) => {
        state.logsLoading = true;
      })
      .addCase(getPricingPlanLogsThunk.fulfilled, (state) => {
        state.logsLoading = false;
      })
      .addCase(getPricingPlanLogsThunk.rejected, (state) => {
        state.logsLoading = false;
      })

      // AddCase For getDeletePricingTypeThunk
      .addCase(getDeletePricingTypeThunk.fulfilled, () => {
        showToast(pricingPlan?.errMsg.item2, showToastType.success);
      })
      .addCase(getDeletePricingTypeThunk.rejected, () => {
        showToast(pricingPlan?.errMsg.item3, showToastType.error);
      })

      // AddCase For getArchivePricingTypeThunk
      .addCase(getArchivePricingTypeThunk.fulfilled, (_, action) => {
        const { message } = action.payload.data;
        showToast(message, showToastType.success);
      })
      .addCase(getArchivePricingTypeThunk.rejected, () => {
        showToast(commonErrMsg?.item1, showToastType.error);
      })

      // AddCase For getToggleDefaultRiskBasedThunk
      .addCase(getToggleDefaultRiskBasedThunk.fulfilled, (_, action) => {
        const { data } = action.payload;
        showToast(data.message, showToastType.success);
      })
      .addCase(getToggleDefaultRiskBasedThunk.rejected, (_, action) => {
        const { response } = action.payload;
        showToast(response.data.message, showToastType.error);
      })

      // AddCase For getToggleActiveStatusThunk
      .addCase(getToggleActiveStatusThunk.fulfilled, (_, action) => {
        const { data } = action.payload;
        showToast(data.message, showToastType.success);
      })
      .addCase(getToggleActiveStatusThunk.rejected, (_, action) => {
        const { response } = action.payload;
        showToast(response.data.message, showToastType.error);
      })

      // AddCase For getvalidateriskrangeThunk
      .addCase(getvalidateriskrangeThunk.fulfilled, (_, action) => {
        const { data } = action.payload;
        showToast(data.message, showToastType.success);
      })
      .addCase(getvalidateriskrangeThunk.rejected, (_, action) => {
        const { response } = action.payload;
        showToast(response.data.message, showToastType.error);
      })

      // AddCase For getPricingPlanUpdateThunk
      .addCase(getPricingPlanUpdateThunk.fulfilled, (state, action) => {
        const { data } = action.payload;
        showToast(data.message, showToastType.success);

        if (data?.updatedPlan) {
          state.items = state.items.map((item) =>
            item.id === data.updatedPlan.id ? data.updatedPlan : item
          );
        }
      })
      .addCase(getPricingPlanUpdateThunk.rejected, (_, action) => {
        const { response } = action.payload || {};
        const errorMessage = response?.data?.errors?.[0] || commonErrMsg?.item4;

        showToast(errorMessage, showToastType.error);
      })

      .addCase(getQuoteStatusData.rejected, (_, action) => {
        const { response } = action.payload;
        showToast(response.data.message, showToastType.error);
      });
  }
});

export const getPricingPlanData = createAsyncThunk(
  "pricingPlan/getPricingPlanData",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getPicingPlanURL, {
        params: payload
      });

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getPricingGroupThunk = createAsyncThunk(
  "pricingPlan/getPricingGroupThunk",
  async (_, { dispatch }) => {
    try {
      const response = await privateAxios.get(getPicingGroupURL);

      const filteredData = response.data.data.filter((item) => !item.deletedAt);
      const sortedData = filteredData.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      dispatch(setPlanGroup(sortedData));
      return sortedData;
    } catch {
      // handle catch here;
    }
  }
);

export const getPricingTypesThunk = createAsyncThunk(
  "pricingPlan/getPricingTypesThunk",
  async (_, { dispatch }) => {
    try {
      const response = await privateAxios.get(getPricingTypesURL);
      dispatch(setPricingTypes(response.data.data));
      return response;
    } catch {
      // handle catch here;
    }
  }
);

export const getClientStatusThunk = createAsyncThunk(
  "pricingPlan/getClientStatusThunk",
  async (_, { dispatch }) => {
    try {
      const response = await privateAxios.get(getClientStatusURL);
      dispatch(setPPCustomerStatus(response.data.data));
      return response;
    } catch {
      // handle catch here;
    }
  }
);

export const getPricingUserPlansThunk = createAsyncThunk(
  "pricingPlan/getPricingUserPlansThunk",
  async (payload) => {
    try {
      const response = await privateAxios.get(getPricingUserPlansURL, {
        params: payload
      });
      return response;
    } catch {
      // handle catch here;
    }
  }
);

export const getPricingPlanLogsThunk = createAsyncThunk(
  "pricingPlan/getPricingPlanLogsThunk",
  async (payload) => {
    try {
      const response = await privateAxios.get(getPricingPlanLogsURL, {
        params: payload
      });
      return response;
    } catch {
      // handle catch here;
    }
  }
);

export const getPricingPlanLogsUserThunk = createAsyncThunk(
  "pricingPlan/getPricingPlanLogsUserThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(
        getPricingPlanLogsUserURL(payload)
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getDeletePricingTypeThunk = createAsyncThunk(
  "pricingPlan/getDeletePricingTypeThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.delete(
        getDeletePricingTypesURL(payload)
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getArchivePricingTypeThunk = createAsyncThunk(
  "pricingPlan/getArchivePricingTypeThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.patch(
        getArchivePricingTypesURL(payload)
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getToggleDefaultRiskBasedThunk = createAsyncThunk(
  "pricingPlan/getToggleDefaultRiskBasedThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.patch(
        getToggleDefaultRiskBasedsURL(payload)
      );

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getToggleActiveStatusThunk = createAsyncThunk(
  "pricingPlan/getToggleActiveStatusThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.patch(
        getToggleActiveStatusURL(payload)
      );

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getvalidateriskrangeThunk = createAsyncThunk(
  "pricingPlan/getvalidateriskrangeThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.post(
        getvalidateriskrangeURL(payload)
      );

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getPricingPlanUpdateThunk = createAsyncThunk(
  "pricingPlan/update",
  async ({ rowId, updateData }, { rejectWithValue }) => {
    try {
      const response = await privateAxios.put(
        getPicingPlanUpdateURL(rowId),
        updateData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getQuoteStatusData = createAsyncThunk(
  "pricingPlan/getQuoteStatusData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getQuotesStatusUrl);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const {
  setPlanGroup,
  setPricingTypes,
  removePricingTypeById,
  setPPCustomerStatus
} = pricingplanSlice.actions;
export default pricingplanSlice.reducer;
